import { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { AvironIcon } from '@/components/ui/aviron-icon';
import { colors, spacing, radius, typography as t } from '@/theme/tokens';
import { useUIStore } from '@/stores/ui.store';
import {
  useSignInHandoffStore,
  RAIL_LOGO_SELECTOR,
  SIGN_IN_HEADLINE,
  SIGN_IN_SUBHEAD,
  type SignInHandoffFrom,
} from '@/stores/sign-in-handoff.store';

/**
 * The moment between signing in and landing on the devices list.
 *
 * Two treatments, chosen in the store by how long it has been since the last
 * visit. The everyday one narrows the login page's dark panel into the sidebar:
 * both are already `colors.sidebar.bg`, and the brand tile only has to move
 * 36px left and 311px up at the default viewport, so the whole transition is
 * one width and one transform. Nothing new is invented; a shape already on
 * screen is reused. After a real absence the brand splash plays instead.
 *
 * Both have to live *outside* the router's `<Suspense>`. `navigate('/')`
 * unmounts `LoginPage` and mounts `MainLayout`, but `/devices` is a lazy chunk
 * and the app's single Suspense boundary wraps the whole `<Routes>` tree with a
 * `null` fallback — so for the length of that chunk fetch there is nothing on
 * screen at all. This overlay is what covers that gap, which is also why it
 * waits for the rail to actually exist before letting go: unmounting into a
 * still-suspended tree would reveal the blank page it exists to hide.
 */

/**
 * The rail's brand block, from `main-layout.tsx`: the logo row is px/py 20 with
 * a 32px tile. `sidebarCollapsedWidth` is 72, so a centred tile lands on the
 * same 20px inset — the destination is (20, 20) in both rail states, and only
 * the wordmark differs.
 */
const RAIL_MARK = { x: 20, y: 20, size: 32 } as const;

const MORPH_MS = 520;
const MORPH_DELAY_MS = 80;
const COPY_MS = 190;
const COPY_DELAY_MS = 100;
const FORM_MS = 150;
const HANDOFF_FADE_MS = 140;
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

/** The splash's own entrance, then how long the finished mark is held. */
const SPLASH_MARK_MS = 430;
const SPLASH_MARK_DELAY_MS = 60;
const SPLASH_WORD_MS = 420;
const SPLASH_WORD_DELAY_MS = 170;
const SPLASH_HOLD_MS = 1140;
const SPLASH_FADE_MS = 360;
const SPLASH_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
const SPLASH_MARK_SIZE = 76;

/** Never hold the cover open on a shell that is not coming. */
const SHELL_WAIT_CAP_MS = 2500;

/** Reduced motion still needs a beat, or the cover is a one-frame flash. */
const REDUCED_HOLD_MS = 160;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

type Phase = 'from' | 'to' | 'out';

/**
 * The lifecycle both treatments share: paint the start state for a frame, flip
 * to the end state so the transitions have something to interpolate from, then
 * let go once the animation has had its time *and* the shell has really
 * painted. `skip` collapses the hold but not the wait for the shell — a viewer
 * dismissing the splash should get to the app early, not to a blank page.
 */
function useCover(holdMs: number, fadeMs: number) {
  const disarm = useSignInHandoffStore((s) => s.disarm);
  const [phase, setPhase] = useState<Phase>('from');
  const [skipped, setSkipped] = useState(false);
  const skip = useCallback(() => setSkipped(true), []);

  useEffect(() => {
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setPhase('to'));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, []);

  useEffect(() => {
    if (phase !== 'to') return;
    const startedAt = performance.now();
    let raf = 0;
    let timer = 0;
    const tick = () => {
      const elapsed = performance.now() - startedAt;
      const shellUp = !!document.querySelector(RAIL_LOGO_SELECTOR);
      const held = skipped || elapsed >= holdMs;
      if (held && (shellUp || elapsed >= SHELL_WAIT_CAP_MS)) {
        setPhase('out');
        timer = window.setTimeout(disarm, fadeMs);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [phase, skipped, holdMs, fadeMs, disarm]);

  return { phase, skip };
}

export function SignInHandoff() {
  const intro = useSignInHandoffStore((s) => s.intro);
  const armId = useSignInHandoffStore((s) => s.armId);

  // Keyed on armId so a second sign-in in one page life mounts a fresh run.
  // Resetting the phase from inside an effect would work too, but it means a
  // cascading render on the very frame the animation is trying to start.
  if (!intro) return null;
  if (intro.mode === 'splash') return <BrandSplash key={armId} />;
  if (!intro.from) return null;
  return <PanelHandoff key={armId} from={intro.from} />;
}

/**
 * The everyday intro: the login panel narrows into the rail and the brand tile
 * rides along, so one object stays continuous across a route change.
 */
function PanelHandoff({ from }: { from: SignInHandoffFrom }) {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const reduced = usePrefersReducedMotion();
  const { phase } = useCover(
    reduced ? REDUCED_HOLD_MS : MORPH_DELAY_MS + MORPH_MS,
    HANDOFF_FADE_MS
  );

  const railW = collapsed
    ? spacing.sidebarCollapsedWidth
    : spacing.sidebarWidth;
  const scale = from.mark.size / RAIL_MARK.size;
  const dx = from.mark.x - RAIL_MARK.x;
  const dy = from.mark.y - RAIL_MARK.y;
  const morph = `${MORPH_MS}ms ${EASE} ${MORPH_DELAY_MS}ms`;

  // Under reduced motion the cover still appears — the alternative is the blank
  // screen it exists to hide — but it appears already settled and just fades.
  const settled = reduced || phase !== 'from';

  return (
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1250, // above MUI's Drawer (1200), below its Modal (1300)
        pointerEvents: 'none',
        opacity: phase === 'out' ? 0 : 1,
        transition: `opacity ${HANDOFF_FADE_MS}ms linear`,
      }}
    >
      {!reduced && (
        <>
          {/* The login page's right-hand form panel, dissolving. */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: from.panelW,
              right: 0,
              bgcolor: 'background.paper',
              opacity: settled ? 0 : 1,
              transition: `opacity ${FORM_MS}ms ease-out`,
            }}
          />

          {/* Headline and subhead, lifting away as the panel passes them. */}
          <Box
            sx={{
              position: 'absolute',
              left: from.copy.x,
              top: from.copy.y,
              width: from.copy.w,
              opacity: settled ? 0 : 1,
              transform: settled ? 'translateY(-10px)' : 'none',
              transition: `opacity ${COPY_MS}ms ease-out ${COPY_DELAY_MS}ms,
                           transform ${COPY_MS}ms ease-out ${COPY_DELAY_MS}ms`,
            }}
          >
            <Typography
              sx={{
                color: colors.sidebar.textActive,
                fontSize: 40,
                fontWeight: t.fontWeight.bold,
                lineHeight: 1.15,
                letterSpacing: '-0.01em',
                fontFamily: t.fontFamily,
                mb: 2,
              }}
            >
              {SIGN_IN_HEADLINE}
            </Typography>
            <Typography
              sx={{
                color: colors.sidebar.text,
                fontSize: t.fontSize.md,
                lineHeight: 1.55,
                fontFamily: t.fontFamily,
                maxWidth: 440,
              }}
            >
              {SIGN_IN_SUBHEAD}
            </Typography>
          </Box>
        </>
      )}

      {/* The dark panel, narrowing into the rail. This is a layout animation
          rather than a composited one, but the element has no children to
          reflow, so there is nothing for it to cost. */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: settled ? railW : from.panelW,
          bgcolor: colors.sidebar.bg,
          transition: reduced ? 'none' : `width ${morph}`,
        }}
      />

      {/* The travelling brand block, drawn at its rail size and scaled up to
          its login size, so the whole morph is one transform on one element.
          Every value here is the rail's, not the login page's: the end state
          has to land exactly on the real thing, and a fractional mismatch at
          the start is a frame nobody sees. */}
      <Box
        sx={{
          position: 'absolute',
          left: RAIL_MARK.x,
          top: RAIL_MARK.y,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          transformOrigin: '0 0',
          transform: settled
            ? 'none'
            : `translate(${dx}px, ${dy}px) scale(${scale})`,
          transition: reduced ? 'none' : `transform ${morph}`,
        }}
      >
        <Box
          sx={{
            width: RAIL_MARK.size,
            height: RAIL_MARK.size,
            // The two tiles are not the same shape today: login writes
            // `${radius.lg}px` (8px) and the rail writes the bare number, which
            // MUI multiplies by shape.borderRadius (4) into 32px — a circle on
            // a 32px box. Rather than silently change either page, each end
            // keeps its own expression and the radius animates between them.
            borderRadius: settled ? radius.lg : `${radius.lg}px`,
            bgcolor: colors.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: reduced ? 'none' : `border-radius ${morph}`,
          }}
        >
          <AvironIcon size={20} color="#fff" />
        </Box>
        <Typography
          sx={{
            color: '#fff',
            fontSize: 15,
            fontWeight: t.fontWeight.bold,
            fontFamily: t.fontFamily,
            whiteSpace: 'nowrap',
            // A collapsed rail has no wordmark to land on, so it leaves early
            // rather than arriving somewhere it does not belong.
            opacity: collapsed && settled ? 0 : 1,
            transition: reduced
              ? 'none'
              : `opacity ${MORPH_MS / 2}ms ease-out ${MORPH_DELAY_MS}ms`,
          }}
        >
          Aviron MDM
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * The rationed intro: a full-bleed brand moment for a first visit or a return
 * after a real absence.
 *
 * It is dismissible. A splash cannot be *automatically* cut short without
 * looking broken, which is the argument against showing one on every sign-in —
 * but someone who has seen it and wants their fleet should not have to wait it
 * out. Dismissing skips the hold, not the wait for the shell.
 */
function BrandSplash() {
  const reduced = usePrefersReducedMotion();
  const { phase, skip } = useCover(
    reduced ? REDUCED_HOLD_MS : SPLASH_HOLD_MS,
    SPLASH_FADE_MS
  );

  useEffect(() => {
    const onKey = () => skip();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [skip]);

  const settled = reduced || phase !== 'from';

  return (
    <Box
      aria-hidden
      onClick={skip}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1250,
        // Opaque and clickable, unlike the handoff: there is nothing behind it
        // to mis-click, and taking the click is what makes it dismissible.
        pointerEvents: phase === 'out' ? 'none' : 'auto',
        cursor: 'pointer',
        bgcolor: colors.sidebar.bg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '22px',
        opacity: phase === 'out' ? 0 : 1,
        transition: `opacity ${SPLASH_FADE_MS}ms ease-in-out`,
      }}
    >
      <Box
        sx={{
          width: SPLASH_MARK_SIZE,
          height: SPLASH_MARK_SIZE,
          // Written in px so MUI leaves it alone, keeping the login page's
          // 1:5 tile-to-radius proportion rather than the rail's accidental
          // circle. See the note in PanelHandoff.
          borderRadius: `${radius.xl}px`,
          bgcolor: colors.primary.main,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: settled ? 1 : 0,
          transform: settled ? 'none' : 'scale(0.72)',
          transition: reduced
            ? 'none'
            : `opacity ${SPLASH_MARK_MS}ms ${SPLASH_EASE} ${SPLASH_MARK_DELAY_MS}ms,
               transform ${SPLASH_MARK_MS}ms ${SPLASH_EASE} ${SPLASH_MARK_DELAY_MS}ms`,
        }}
      >
        <AvironIcon size={46} color="#fff" />
      </Box>
      <Typography
        sx={{
          color: colors.sidebar.textActive,
          fontSize: 26,
          fontWeight: t.fontWeight.bold,
          fontFamily: t.fontFamily,
          opacity: settled ? 1 : 0,
          letterSpacing: settled ? '0.01em' : '0.26em',
          transition: reduced
            ? 'none'
            : `opacity ${SPLASH_WORD_MS}ms ${SPLASH_EASE} ${SPLASH_WORD_DELAY_MS}ms,
               letter-spacing ${SPLASH_WORD_MS}ms ${SPLASH_EASE} ${SPLASH_WORD_DELAY_MS}ms`,
        }}
      >
        Aviron MDM
      </Typography>
    </Box>
  );
}

export default SignInHandoff;
